/**
 * RC-152c-2a + RC-152c-2a-style — Form Designer · form canvas
 * (center column).
 *
 * Renders the placed fields[] as a vertical list of compact field
 * cards. Each card shows the operator-facing label, the type +
 * required marker, and the on-the-wire {@code name} (the key
 * downstream nodes interpolate via {@code ${nodeId.output.name}}).
 *
 * Card interactions:
 *   • Click → select (selected card gets a navy accent ring; parent
 *     surfaces the selected field in the FieldInspector).
 *   • Arrow-up / arrow-down → reorder (disabled at the boundaries).
 *   • Trash → remove (immediate; undo via the canvas-side workflow
 *     versions slideout for now — designer-local undo is a future
 *     enhancement).
 *
 * <p><b>Style contract (RC-152c-2a-style operator directive,
 * 2026-06-21):</b> All visual styling comes from V2 design tokens
 * via Tailwind utility classes. No inline {@code style={{}}}
 * objects, no hand-rolled hex.</p>
 */

import React from 'react';
import {
  Type, AlignLeft, Hash, Calendar, Clock,
  ChevronDown, ListChecks, Check, Upload,
  ArrowUp, ArrowDown, Trash2,
} from 'lucide-react';

const ICONS = {
  text: Type,
  textarea: AlignLeft,
  number: Hash,
  date: Calendar,
  datetime: Clock,
  select: ChevronDown,
  'multi-select': ListChecks,
  checkbox: Check,
  file: Upload,
};

export default function FormCanvas({
  fields,
  selectedIndex,
  onSelectField,
  onMoveField,
  onRemoveField,
  readOnly = false,
}) {
  return (
    <main
      aria-label="Form composition"
      className="p-4 bg-v2-surface overflow-y-auto"
    >
      <div className="text-[10px] text-v2-ink-muted uppercase tracking-wide font-semibold mb-2.5">
        Form composition · click a field to edit · use the arrows to reorder
      </div>

      {(!fields || fields.length === 0) && (
        <div className="p-6 border border-dashed border-v2-border rounded-v2-md text-center text-[12px] text-v2-ink-muted leading-relaxed">
          No fields yet. Click a type in the palette on the left to add your first field.
        </div>
      )}

      {fields && fields.length > 0 && (
        <ul className="list-none p-0 m-0 flex flex-col gap-2">
          {fields.map((field, i) => {
            const selected = i === selectedIndex;
            const Icon = ICONS[(field.type || 'text').toLowerCase()] || Type;
            const isFirst = i === 0;
            const isLast = i === fields.length - 1;
            return (
              <li key={field._uid || `${field.name || 'field'}_${i}`}>
                <div
                  onClick={() => onSelectField(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectField(i); } }}
                  className={
                    'v2-focus-ring p-2.5 rounded-v2-md bg-v2-surface cursor-pointer flex flex-col gap-1.5 ' +
                    'transition-[box-shadow,border-color] duration-v2 ease-v2 ' +
                    (selected
                      ? 'border-2 border-v2-action shadow-v2-sm'
                      : 'border border-v2-border-subtle hover:border-v2-border')
                  }
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className={
                      'flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold ' +
                      (selected ? 'text-v2-action' : 'text-v2-ink-muted')
                    }>
                      <Icon size={12} aria-hidden="true" />
                      <span>{(field.type || 'text').toLowerCase()}</span>
                      {field.required && <span>· required</span>}
                      {selected && <span>· selected</span>}
                    </div>
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1"
                    >
                      <IconBtn
                        title="Move up"
                        ariaLabel="Move field up"
                        disabled={readOnly || isFirst}
                        onClick={() => onMoveField(i, -1)}
                      >
                        <ArrowUp size={13} aria-hidden="true" />
                      </IconBtn>
                      <IconBtn
                        title="Move down"
                        ariaLabel="Move field down"
                        disabled={readOnly || isLast}
                        onClick={() => onMoveField(i, 1)}
                      >
                        <ArrowDown size={13} aria-hidden="true" />
                      </IconBtn>
                      <IconBtn
                        title="Remove field"
                        ariaLabel="Remove field"
                        disabled={readOnly}
                        onClick={() => onRemoveField(i)}
                        danger
                      >
                        <Trash2 size={13} aria-hidden="true" />
                      </IconBtn>
                    </div>
                  </div>
                  <div className="text-[13px] font-medium text-v2-ink">
                    {field.label || <span className="text-v2-ink-muted">(unlabeled)</span>}
                  </div>
                  <div className="text-[11px] text-v2-ink-muted font-mono">
                    {field.name || <span className="italic">(unnamed — fill in via inspector)</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}

function IconBtn({ children, title, ariaLabel, disabled, danger, onClick }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={
        'v2-focus-ring w-6 h-6 inline-flex items-center justify-center rounded-v2-sm ' +
        'bg-transparent border border-transparent transition-colors duration-v2 ease-v2 ' +
        'hover:bg-v2-muted ' +
        (danger ? 'text-v2-brand' : 'text-v2-ink-secondary') + ' ' +
        'disabled:cursor-not-allowed disabled:opacity-35'
      }
    >
      {children}
    </button>
  );
}
